import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import { getActivities } from '../api/trainer';
import { ActivityQuery } from '../api/types';
import PageHeader from '../components/PageHeader';
import StatusMessage from '../components/StatusMessage';
import { useAsync } from '../hooks/useAsync';
import {
  ACTIVITY_TYPE_OPTIONS,
  activityTypeLabel,
  formatDate,
  formatDistance,
  formatDuration,
  formatNumber,
  formatPace,
} from '../utils/format';

const PAGE_SIZE = 20;

const ActivitiesPage: React.FC = () => {
  const [type, setType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);

  const query: ActivityQuery = {
    page,
    size: PAGE_SIZE,
    ...(type ? { type } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  };

  const list = useAsync(() => getActivities(query), [type, from, to, page]);
  const result = list.data;

  const changeFilter = (setter: (value: string) => void, value: string) => {
    setter(value);
    setPage(0);
  };

  return (
    <>
      <PageHeader title="Activities">
        <p>Runs and other sessions synced from Garmin. Select one for pace, heart rate and laps.</p>
      </PageHeader>

      <section className="surface mb-3">
        <Form>
          <Row className="g-2 align-items-end">
            <Col xs={12} sm={4} md={3}>
              <Form.Label htmlFor="activity-type" className="small muted">Type</Form.Label>
              <Form.Select id="activity-type" value={type} onChange={e => changeFilter(setType, e.target.value)}>
                <option value="">All types</option>
                {ACTIVITY_TYPE_OPTIONS.map(t => (
                  <option key={t} value={t}>{activityTypeLabel(t)}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} sm={3} md={3}>
              <Form.Label htmlFor="activity-from" className="small muted">From</Form.Label>
              <Form.Control id="activity-from" type="date" value={from} onChange={e => changeFilter(setFrom, e.target.value)} />
            </Col>
            <Col xs={6} sm={3} md={3}>
              <Form.Label htmlFor="activity-to" className="small muted">To</Form.Label>
              <Form.Control id="activity-to" type="date" value={to} onChange={e => changeFilter(setTo, e.target.value)} />
            </Col>
            <Col xs={12} sm={2} md={3} className="d-grid">
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setType('');
                  setFrom('');
                  setTo('');
                  setPage(0);
                }}
              >
                Clear filters
              </Button>
            </Col>
          </Row>
        </Form>
      </section>

      <section className="surface">
        <StatusMessage loading={list.loading} error={list.error} onRetry={list.reload} />
        {result && result.items.length === 0 && !list.loading && <p className="muted mb-0">No activities match these filters.</p>}
        {result && result.items.length > 0 && (
          <>
            <div className="table-responsive">
              <Table hover className="trainer-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Activity</th>
                    <th className="text-end">Distance</th>
                    <th className="text-end">Time</th>
                    <th className="text-end">Pace</th>
                    <th className="text-end">Avg HR</th>
                  </tr>
                </thead>
                <tbody>
                  {result.items.map(a => (
                    <tr key={a.id}>
                      <td className="tabular">{formatDate(a.startTime)}</td>
                      <td>
                        <Link to={`/activities/${a.id}`}>{a.name ?? 'Untitled activity'}</Link>
                        <div className="stat-hint">{activityTypeLabel(a.activityType)}</div>
                      </td>
                      <td className="text-end tabular">{formatDistance(a.distanceM)}</td>
                      <td className="text-end tabular">{formatDuration(a.durationS)}</td>
                      <td className="text-end tabular">{formatPace(a.avgPaceSPerKm)}</td>
                      <td className="text-end tabular">{formatNumber(a.avgHr)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <div className="d-flex justify-content-between align-items-center mt-3 gap-2">
              <Button variant="outline-secondary" size="sm" disabled={page === 0 || list.loading} onClick={() => setPage(p => p - 1)}>
                Previous
              </Button>
              <span className="muted small tabular">
                Page {result.page + 1} of {Math.max(1, result.totalPages)} · {result.totalElements} activities
              </span>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={page + 1 >= result.totalPages || list.loading}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </Button>
            </div>
          </>
        )}
      </section>
    </>
  );
};

export default ActivitiesPage;
